/**
 * The one overview route.
 *
 * `GET /api/tenants/{tenant}/dashboards/overview` — a single request populates
 * the entire screen, so the page gets one loading state and one cache key.
 * Tenant rides the path; the shared `api` client threads auth, the tenant
 * header and Sentry trace headers, and supplies the `/air` prefix via its
 * `baseURL`.
 *
 * Per P-03 the GET takes a `signal`.
 */
import { api } from '@/lib/api'

import type { OverviewDashboardWire } from './types'
import type { ResolvedWindow } from './window'

/**
 * Fetch the overview payload for a resolved window.
 *
 * Both bounds are always sent. Omitting them is legal — the server defaults to
 * the last 30 days — but then the window the response describes depends on when
 * it arrived, which is precisely the ambiguity the echoed `window` exists to
 * remove.
 *
 * A window wider than the server maximum, or with `start >= end`, comes back as
 * a 400 whose message is written to be shown to a user; the filter bar surfaces
 * it inline rather than letting it reach the error boundary.
 */
export async function fetchOverviewDashboard(
	tenant: string,
	window: ResolvedWindow,
	signal?: AbortSignal,
): Promise<OverviewDashboardWire> {
	const search = new URLSearchParams({
		start_time: window.start,
		end_time: window.end,
	})
	const { data } = await api.get<OverviewDashboardWire>(
		`/api/tenants/${tenant}/dashboards/overview?${search}`,
		{ signal },
	)
	return data
}
